import type { AH_ToolQuestionPayload, AhToolQuestionOutcome } from '@numeo/types'

/**
 * Where a card's question stands on this page: waiting for the user (`open`), being sent, answered here (`accepted`,
 * `declined`), answered on another page (`answered`), or stopped waiting without an answer from here (`withdrawn`,
 * `expired`, `gone`).
 */
export type ToolQuestionStatus = 'open' | 'sending' | 'accepted' | 'declined' | 'answered' | 'withdrawn' | 'expired' | 'gone'

/** A card's question (ADR-048 decisions 9-11) as the chat shows it. */
export interface ToolQuestion {
    tabId: string
    questionId: string
    /** The card's question, word for word. */
    cardMessage: string
    form: AH_ToolQuestionPayload['form']
    /** ISO time after which the server refuses an answer. */
    expiresAt: string
    status: ToolQuestionStatus
    /** Why the last answer did not go through (it can be sent again). */
    error?: string
    /** The user sent a new message while it was open, which withdraws it on the server. */
    superseded?: boolean
}

export type ToolQuestionMap = Record<string, ToolQuestion>

/** The user may still answer it: open, and not left behind by a message they sent since (the server withdraws it). */
export const isAnswerable = (question: ToolQuestion): boolean => question.status === 'open' && !question.superseded

/** It was answered or stopped waiting: the agent's next reply says what happened, so the card can go. */
const isSettled = (question: ToolQuestion): boolean => question.status !== 'open' && question.status !== 'sending' && question.status !== 'expired'

/**
 * A question arrived (live, replayed after a reconnect, or read on open). A replay of one being sent keeps the send;
 * any other copy shows it open again: the server asks again only when the earlier answer did not reach the card.
 */
export function upsertQuestion(questions: ToolQuestionMap, payload: AH_ToolQuestionPayload): ToolQuestionMap {
    const known = questions[payload.questionId]
    if (known?.status === 'sending') return questions
    const { tabId, questionId, cardMessage, form, expiresAt } = payload
    return { ...questions, [questionId]: { tabId, questionId, cardMessage, form, expiresAt, status: 'open' } }
}

/** The server says the question stopped waiting (`AH_ToolQuestionResolved`). An answer given here keeps its wording. */
export function resolveQuestion(questions: ToolQuestionMap, questionId: string, outcome: AhToolQuestionOutcome): ToolQuestionMap {
    const known = questions[questionId]
    if (!known) return questions
    if (outcome === 'answered' && (known.status === 'accepted' || known.status === 'declined' || known.status === 'sending')) return questions
    // The user moved on with a new message, and the server let this question go: its card goes too. Shown as "expired" it would
    // sit under the newer turn's reply, out of place, for a question the user chose not to answer.
    if (known.superseded && outcome !== 'answered') return Object.fromEntries(Object.entries(questions).filter(([id]) => id !== questionId))
    return { ...questions, [questionId]: { ...known, status: outcome, error: undefined } }
}

/** Sets one question's status (and the error of a failed answer). */
export function patchQuestion(questions: ToolQuestionMap, questionId: string, status: ToolQuestionStatus, error?: string): ToolQuestionMap {
    const known = questions[questionId]
    return known ? { ...questions, [questionId]: { ...known, status, error } } : questions
}

/** The server waits on `waitingId` only (or on none): the tab's other open cards stopped waiting while we did not hear it. */
export function closeMissing(questions: ToolQuestionMap, tabId: string, waitingId: string | undefined): ToolQuestionMap {
    const stale = Object.values(questions).filter((q) => q.tabId === tabId && q.status === 'open' && q.questionId !== waitingId)
    return stale.reduce((acc, q) => patchQuestion(acc, q.questionId, 'gone'), questions)
}

/** Drops the tab's settled questions once a newer reply or message follows them. */
export function pruneSettled(questions: ToolQuestionMap, tabId: string): ToolQuestionMap {
    const kept = Object.values(questions).filter((q) => q.tabId !== tabId || !isSettled(q))
    return kept.length === Object.keys(questions).length ? questions : Object.fromEntries(kept.map((q) => [q.questionId, q]))
}

/**
 * Drops every question of the tab that is not answerable any more, and marks the open ones superseded: a new message withdraws
 * an open one on the server, and when it says so the card goes (`resolveQuestion`).
 */
export function pruneForMessage(questions: ToolQuestionMap, tabId: string): ToolQuestionMap {
    const kept = Object.values(questions).filter((q) => q.tabId !== tabId || q.status === 'open' || q.status === 'sending')
    return Object.fromEntries(kept.map((q) => [q.questionId, q.tabId === tabId && q.status === 'open' ? { ...q, superseded: true } : q]))
}

/** Takes back `pruneForMessage`'s mark on the tab's open questions: the message never reached the server, which still waits on them. */
export function restoreAfterFailedMessage(questions: ToolQuestionMap, tabId: string): ToolQuestionMap {
    return Object.fromEntries(
        Object.entries(questions).map(([id, q]) => [id, q.tabId === tabId && q.status === 'open' && q.superseded ? { ...q, superseded: false } : q])
    )
}

/** The tab's questions, in the order they arrived. */
export const questionsOfTab = (questions: ToolQuestionMap, tabId: string | null): ToolQuestion[] =>
    tabId ? Object.values(questions).filter((q) => q.tabId === tabId) : []
